import { DRXError, isDRXError } from '@/lib/drx-error';
import type { FavoriteRepository } from '../repository/favorite.repository';
import type { IFavoriteUseCase } from './interfaces/favorite.usecase.interface';
import type {
  FavoriteTargetType,
  FavoriteFilter,
  GetFavoriteData,
} from '../repository/interfaces/favorite.repository.interface';

export class FavoriteUseCase implements IFavoriteUseCase {
  constructor(private favoriteRepository: FavoriteRepository) {}

  async addFavorite(
    userId: string,
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<string> {
    try {
      if (!userId || !targetId) {
        throw new DRXError({
          code: 'FAVORITE_INVALID_INPUT',
          devMessage: 'userId and targetId are required to add a favorite',
          userMessage: 'Missing required information to add favorite.',
          level: 'warning',
        });
      }

      return await this.favoriteRepository.addFavorite(userId, targetId, targetType);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_ADD_FAILED',
        devMessage: `Unexpected error adding favorite ${targetType}/${targetId} for user ${userId}`,
        userMessage: 'Could not add to favorites. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async removeFavorite(
    userId: string,
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<void> {
    try {
      if (!userId || !targetId) {
        throw new DRXError({
          code: 'FAVORITE_INVALID_INPUT',
          devMessage: 'userId and targetId are required to remove a favorite',
          userMessage: 'Missing required information to remove favorite.',
          level: 'warning',
        });
      }

      await this.favoriteRepository.removeFavorite(userId, targetId, targetType);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_REMOVE_FAILED',
        devMessage: `Unexpected error removing favorite ${targetType}/${targetId} for user ${userId}`,
        userMessage: 'Could not remove from favorites. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async toggleFavorite(
    userId: string,
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<boolean> {
    try {
      if (!userId || !targetId) {
        throw new DRXError({
          code: 'FAVORITE_INVALID_INPUT',
          devMessage: 'userId and targetId are required to toggle a favorite',
          userMessage: 'Missing required information to update favorite.',
          level: 'warning',
        });
      }

      return await this.favoriteRepository.toggleFavorite(userId, targetId, targetType);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_TOGGLE_FAILED',
        devMessage: `Unexpected error toggling favorite ${targetType}/${targetId} for user ${userId}`,
        userMessage: 'Could not update favorite status. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async checkIfFavorited(
    userId: string,
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<boolean> {
    try {
      if (!userId || !targetId) {
        throw new DRXError({
          code: 'FAVORITE_INVALID_INPUT',
          devMessage: 'userId and targetId are required to check favorite status',
          userMessage: 'Missing required information.',
          level: 'warning',
        });
      }

      return await this.favoriteRepository.checkIfFavorited(userId, targetId, targetType);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_CHECK_FAILED',
        devMessage: `Unexpected error checking favorite status for ${targetType}/${targetId}, user ${userId}`,
        userMessage: 'Could not check favorite status.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getUserFavorites(
    userId: string,
    filter?: FavoriteFilter,
  ): Promise<GetFavoriteData[]> {
    try {
      if (!userId) {
        throw new DRXError({
          code: 'FAVORITE_INVALID_INPUT',
          devMessage: 'userId is required to get favorites',
          userMessage: 'Missing required information.',
          level: 'warning',
        });
      }

      return await this.favoriteRepository.getUserFavorites(userId, filter);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_FETCH_FAILED',
        devMessage: `Unexpected error fetching favorites for user ${userId}`,
        userMessage: 'Could not load your favorites. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getTargetFavoritesCount(
    targetId: string,
    targetType: FavoriteTargetType,
  ): Promise<number> {
    try {
      if (!targetId) {
        throw new DRXError({
          code: 'FAVORITE_INVALID_INPUT',
          devMessage: 'targetId is required to get favorites count',
          userMessage: 'Missing required information.',
          level: 'warning',
        });
      }

      return await this.favoriteRepository.getTargetFavoritesCount(targetId, targetType);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_COUNT_FAILED',
        devMessage: `Unexpected error getting favorites count for ${targetType}/${targetId}`,
        userMessage: 'Could not load favorites count.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getFavoritesCountsForIds(
    targetIds: string[],
    targetType: FavoriteTargetType,
  ): Promise<Record<string, number>> {
    try {
      return await this.favoriteRepository.getFavoritesCountsForIds(targetIds, targetType);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_BATCH_COUNT_FAILED',
        devMessage: `Unexpected error batch-counting favorites for ${targetType}`,
        userMessage: 'Could not load favorites counts.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getTargetFavoriters(
    targetId: string,
    targetType: FavoriteTargetType,
    limitCount?: number,
  ): Promise<string[]> {
    try {
      if (!targetId) {
        throw new DRXError({
          code: 'FAVORITE_INVALID_INPUT',
          devMessage: 'targetId is required to get favoriters',
          userMessage: 'Missing required information.',
          level: 'warning',
        });
      }

      return await this.favoriteRepository.getTargetFavoriters(targetId, targetType, limitCount);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_FAVORITERS_FAILED',
        devMessage: `Unexpected error getting favoriters for ${targetType}/${targetId}`,
        userMessage: 'Could not load favoriters.',
        cause: error,
        level: 'error',
      });
    }
  }

  async cleanupOrphanedFavorites(userId: string): Promise<number> {
    try {
      if (!userId) {
        throw new DRXError({
          code: 'FAVORITE_INVALID_INPUT',
          devMessage: 'userId is required for orphan cleanup',
          userMessage: 'Missing required information.',
          level: 'warning',
        });
      }

      return await this.favoriteRepository.cleanupOrphanedFavorites(userId);
    } catch (error) {
      if (isDRXError(error)) throw error;
      throw new DRXError({
        code: 'FAVORITE_CLEANUP_FAILED',
        devMessage: `Unexpected error cleaning up orphaned favorites for user ${userId}`,
        userMessage: 'Could not clean up old favorites.',
        cause: error,
        level: 'error',
      });
    }
  }
}
